import { useField } from '@framework/form/field/useField.tsx';
import { ColorPickerInput } from '@framework/form/inputs/ColorPickerInput.tsx';
import { colorPickerPresets } from '@framework/form/inputs/colorPickerPresets.ts';
import { SelectInput } from '@framework/form/inputs/SelectInput.tsx';
import { SliderNumberInput } from '@framework/form/inputs/SliderNumberInput.tsx';
import { SwitchInput } from '@framework/form/inputs/SwitchInput.tsx';
import { TextInput } from '@framework/form/inputs/TextInput.tsx';
import { FormItem } from '@framework/form/item/FormItem.tsx';
import { RowElement } from '@framework/ui/components/row/RowElement.tsx';
import { DividerTitleElement } from '@framework/ui/components/text/DividerTitleElement.tsx';
import { useCallback, useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { sanitizeCode } from '../../../../utils/sanitizeCode.ts';
import { isIntersectionAllowedByFlags } from '../layers/getIntersectionLayersInfo.ts';
import { type IMapGroup } from '../layers/MapGroup.ts';
import {
  DefaultMapLayerMinZoom,
  DefaultMapSymbolSize,
  isCompetenceCapableLayerType,
  isIntersectableLayerType,
  isQueryableLayerType,
  isVectorLayerType,
  MapLayerType,
  MapLayerTypeOptions,
  MapSymbolShapeOptions,
} from '../layers/MapLayer.ts';
import { MapWidgetLogic } from '../MapWidgetLogic.ts';
import { MapLayerCompetenceConfigInput } from './competence/MapLayerCompetenceConfigInput.tsx';
import { MapLayerIdentifyConfigInput } from './identify/MapLayerIdentifyConfigInput.tsx';
import { MapLayerIntersectionConfigInput } from './intersection/MapLayerIntersectionConfigInput.tsx';
import { MapLayerStylePreview } from './preview/MapLayerStylePreview.tsx';
import { MapLayerServiceStatus } from './status/MapLayerServiceStatus.tsx';
import { MapLayerWfsConfigInput } from './wfs/MapLayerWfsConfigInput.tsx';

export interface IMapLayerConfigInputProps {
  prefix: `${string}.` | '';
  allowedTypes?: MapLayerType[];
  groups?: IMapGroup[];
}

/**
 * Permette di definire le proprietà di un singolo livello della mappa.
 */
export function MapLayerConfigInput(props: IMapLayerConfigInputProps) {
  const { prefix: rawPrefix, allowedTypes, groups } = props;
  const prefix = rawPrefix as string;
  const type: MapLayerType = useWatch({ name: `${prefix}type` });
  const intersectionEnabled = useWatch({
    name: `${prefix}intersection.enabled`,
  });
  const isCadastralPreset = useWatch({
    name: `${prefix}intersection.cadastralPreset`,
  });
  const identifyEnabled = useWatch({
    name: `${prefix}identify.enabled`,
  });
  const competenceEnabled = useWatch({
    name: `${prefix}competence.enabled`,
  });
  const layerStyle = useWatch({ name: `${prefix}style` });

  const { getValue: getCode, setValue: setCode } = useField(`${prefix}code`);

  /**
   * Al blur del nome, se il code è ancora vuoto lo compila derivandolo dal
   * nome (una sola volta: se il code è già valorizzato non lo tocca).
   */
  const handleNameBlur = useCallback(
    (value: string | null | undefined) => {
      if (getCode() || !value) return;
      setCode(sanitizeCode(value) || 'layer', {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });
    },
    [getCode, setCode],
  );

  const allowedOptions = useMemo(() => {
    return allowedTypes
      ? MapLayerTypeOptions.filter(o => allowedTypes.includes(o.value))
      : MapLayerTypeOptions;
  }, [allowedTypes]);

  const groupOptions = useMemo(() => {
    return (groups ?? []).map(group => ({
      value: group.name,
      label: group.label || group.name,
    }));
  }, [groups]);

  return (
    <div>
      <h3>Configurazione Livello</h3>
      <FormItem label="Tipo" name={`${prefix}type`}>
        <SelectInput name={`${prefix}type`} options={allowedOptions} />
      </FormItem>
      <FormItem label="Nome" name={`${prefix}name`}>
        <TextInput name={`${prefix}name`} onBlur={handleNameBlur} />
      </FormItem>
      {/* Precompilato dal nome al blur se vuoto; reso univoco tra i layer
          all'apply (assignUniqueLayerCode). */}
      <FormItem
        label="Codice"
        name={`${prefix}code`}
        help="Il codice in caso di intersezioni e individuazioni competenze viene utilizzato per identificare i valori salvati nelle Sezioni. Se modificato, i dati non saranno più associati."
      >
        <TextInput name={`${prefix}code`} />
      </FormItem>
      <FormItem label="URL" name={`${prefix}url`}>
        <TextInput name={`${prefix}url`} />
      </FormItem>
      {type === MapLayerType.WFS && <MapLayerWfsConfigInput prefix={prefix} />}
      <MapLayerServiceStatus prefix={prefix} />
      {groupOptions.length > 0 && (
        <FormItem
          label="Gruppo"
          name={`${prefix}group`}
          help="Gruppo tematico a cui appartiene il layer nel pannello livelli"
        >
          <SelectInput
            name={`${prefix}group`}
            options={groupOptions}
            allowClear
            placeholder="Nessun gruppo (sezione 'Altri')"
          />
        </FormItem>
      )}
      <DividerTitleElement title="Stile" />
      <FormItem label="Colore di riempimento" name={`${prefix}style.fillColor`}>
        <ColorPickerInput
          name={`${prefix}style.fillColor`}
          presets={colorPickerPresets}
        />
      </FormItem>
      <FormItem label="Colore del bordo" name={`${prefix}style.strokeColor`}>
        <ColorPickerInput
          name={`${prefix}style.strokeColor`}
          presets={colorPickerPresets}
        />
      </FormItem>
      <FormItem label="Spessore del bordo" name={`${prefix}style.strokeWidth`}>
        <SliderNumberInput
          min={0}
          max={10}
          step={1}
          precision={0}
          unit="px"
          name={`${prefix}style.strokeWidth`}
        />
      </FormItem>
      <FormItem
        label="Opacità"
        name={`${prefix}style.opacity`}
        help="Un valore pari a 0 renderizza il livello completamente trasparente, un valore pari a 1 completamente opaco"
      >
        <SliderNumberInput
          min={0}
          max={1}
          step={0.1}
          precision={2}
          name={`${prefix}style.opacity`}
        />
      </FormItem>
      {isVectorLayerType(type) && (
        <>
          <FormItem
            label="Simbolo"
            name={`${prefix}style.symbol.shape`}
            help="Forma con cui vengono rese le geometrie puntiformi del layer. Se non specificato viene utilizzato un cerchio."
          >
            <SelectInput
              name={`${prefix}style.symbol.shape`}
              options={MapSymbolShapeOptions}
              allowClear
              placeholder="● Cerchio (default)"
            />
          </FormItem>
          {layerStyle?.symbol?.shape && (
            <FormItem
              label="Dimensione simbolo"
              name={`${prefix}style.symbol.size`}
              help="Raggio del simbolo in pixel"
            >
              <SliderNumberInput
                min={2}
                max={20}
                step={1}
                precision={0}
                unit="px"
                name={`${prefix}style.symbol.size`}
                placeholder={String(DefaultMapSymbolSize)}
              />
            </FormItem>
          )}
        </>
      )}
      <MapLayerStylePreview
        style={layerStyle}
        showSymbol={isVectorLayerType(type)}
      />
      {isVectorLayerType(type) && (
        <FormItem
          label="Etichetta"
          name={`${prefix}labelPropertyName`}
          help="Se specificato, verrà visualizzato un'etichetta per ogni geometria del layer. Il valore dell'etichetta sarà preso dalla proprietà indicata."
        >
          <TextInput name={`${prefix}labelPropertyName`} />
        </FormItem>
      )}
      <RowElement>
        <FormItem
          label="Zoom Minimo"
          name={`${prefix}minZoom`}
          help="In caso di layer vettoriali con elevato numero di Geometrie, è possibile configurare questo valore per attivare il layer solamente dopo il livello di zoom indicato. Lo zoom minimo è 0, lo zoom massimo è 20"
        >
          <SliderNumberInput
            min={0}
            max={20}
            step={1}
            precision={0}
            name={`${prefix}minZoom`}
            placeholder={String(DefaultMapLayerMinZoom)}
          />
        </FormItem>
      </RowElement>
      {isIntersectionAllowedByFlags({ cadastralPreset: isCadastralPreset }) &&
        isIntersectableLayerType(type) && (
          <>
            <DividerTitleElement title="Intersezione" />
            <FormItem label="" name={`${prefix}intersection.enabled`}>
              <SwitchInput name={`${prefix}intersection.enabled`}>
                Abilita intersezione
              </SwitchInput>
            </FormItem>
            {intersectionEnabled && (
              <MapLayerIntersectionConfigInput prefix={prefix} />
            )}
          </>
        )}
      {MapWidgetLogic.isAdvancedMapWidgetEnabled() &&
        isQueryableLayerType(type) && (
          <>
            <DividerTitleElement title="Interrogazione (Identify)" />
            <FormItem label="" name={`${prefix}identify.enabled`}>
              <SwitchInput name={`${prefix}identify.enabled`}>
                Abilita interrogazione
              </SwitchInput>
            </FormItem>
            {identifyEnabled && <MapLayerIdentifyConfigInput prefix={prefix} />}
          </>
        )}
      {isCompetenceCapableLayerType(type) && (
        <>
          <DividerTitleElement title="Competenza" />
          <FormItem label="" name={`${prefix}competence.enabled`}>
            <SwitchInput name={`${prefix}competence.enabled`}>
              Abilita competenza
            </SwitchInput>
          </FormItem>
          {competenceEnabled && (
            <MapLayerCompetenceConfigInput prefix={prefix} />
          )}
        </>
      )}
    </div>
  );
}
